import { useState } from 'react';
import { Callout, Drawer, Table } from '../primitives';
import type { DistributionIssue, PayloadPreview } from '../../api';
import {
  forceSyncDistributionProduct,
  getDistributionProduct,
  previewDistributionPayload,
  retryDistributionPublication,
} from '../../api';
import { useResource } from '../../hooks';
import { LevelBadge } from '../project/ui';
import { ErrorNotice, LoadingRows } from '../ui';
import {
  channelLabel,
  formatMoney,
  formatWhen,
  publicationStateHelp,
  publicationStateLabel,
  publicationStateLevel,
  severityLabel,
  severityLevel,
} from './labels';

/**
 * One product, everything about it: what the shop says, where it has been
 * distributed, what each channel thinks of it, and — the part that actually
 * ends support conversations — the exact payload each channel receives and
 * where every field in it came from.
 */
export function DistributionProductDrawer({
  projectionId,
  canManage,
  onClose,
  onChanged,
}: {
  projectionId: string;
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detail = useResource(() => getDistributionProduct(projectionId), [projectionId], projectionId);
  const [previewFor, setPreviewFor] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const act = async (key: string, action: () => Promise<unknown>) => {
    setBusy(key);
    setError(null);
    try {
      await action();
      detail.reload();
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(null);
    }
  };

  const projection = detail.data?.projection;

  return (
    <Drawer
      open
      size="wide"
      title={projection?.canonical.content.title ?? 'Product'}
      description={
        projection ? (
          <>
            <code>{projection.source_sku}</code> · {projection.website_code}/{projection.store_view_code} · version {projection.version}
          </>
        ) : undefined
      }
      onClose={onClose}
      testId="distribution-product"
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose}>
            Close
          </button>
          {canManage && projection ? (
            <button
              type="button"
              className="button button--primary"
              disabled={busy === 'sync'}
              onClick={() => act('sync', () => forceSyncDistributionProduct(projectionId))}
              data-testid="distribution-force-sync"
            >
              {busy === 'sync' ? 'Reading' : 'Read from the shop and send again'}
            </button>
          ) : null}
        </>
      }
    >
          {detail.error ? <ErrorNotice error={detail.error} /> : null}
          {error ? <Callout tone="danger">{error}</Callout> : null}
          {detail.loading && !detail.data ? <LoadingRows label="Loading product" rows={5} /> : null}

          {projection ? (
            <>
              <section aria-labelledby="distribution-source-title">
                <h3 id="distribution-source-title">From the shop</h3>
                <dl className="fact-list">
                  <div className="fact-list__row">
                    <dt>Price on this website</dt>
                    <dd>{formatMoney(projection.canonical.commerce.final_price)}</dd>
                  </div>
                  <div className="fact-list__row">
                    <dt>Availability</dt>
                    <dd>{projection.canonical.commerce.availability.replace(/_/g, ' ')}</dd>
                  </div>
                  <div className="fact-list__row">
                    <dt>Brand</dt>
                    <dd>{projection.canonical.identity.brand || 'Not set in the shop'}</dd>
                  </div>
                  <div className="fact-list__row">
                    <dt>GTIN</dt>
                    <dd>{projection.canonical.identity.gtin || 'Not set in the shop'}</dd>
                  </div>
                  <div className="fact-list__row">
                    <dt>Manufacturer product number</dt>
                    <dd>{projection.canonical.identity.mpn || 'Not set in the shop'}</dd>
                  </div>
                  <div className="fact-list__row">
                    <dt>Storefront URL</dt>
                    <dd>
                      {projection.canonical.urls.canonical ? (
                        <a href={projection.canonical.urls.canonical} target="_blank" rel="noreferrer noopener">
                          {projection.canonical.urls.canonical}
                        </a>
                      ) : (
                        'Not set in the shop'
                      )}
                    </dd>
                  </div>
                  <div className="fact-list__row">
                    <dt>Last change in the shop</dt>
                    <dd>{formatWhen(projection.source_updated_at)}</dd>
                  </div>
                </dl>
              </section>

              <section aria-labelledby="distribution-channels-status">
                <h3 id="distribution-channels-status">Channels</h3>
                {detail.data?.publications.length === 0 ? (
                  <p className="quiet-empty">Not in the scope of any channel</p>
                ) : (
                  <div className="distribution-publications">
                    {detail.data?.publications.map(({ publication, channel_name, scope }) => (
                      <article key={publication.id} className="distribution-publication">
                        <header className="distribution-publication__head">
                          <div>
                            <h4>{channelLabel[publication.channel_type] ?? channel_name}</h4>
                            <p className="quiet-empty">{scope}</p>
                          </div>
                          <LevelBadge level={publicationStateLevel[publication.state]}>
                            {publicationStateLabel[publication.state]}
                          </LevelBadge>
                        </header>
                        {/* A feed channel reports no per-product state,
                            and saying so is the honest version of "live". */}
                        {publicationStateHelp[publication.state] ||
                        (publication.remote_status === 'not_reported' &&
                          (publication.state === 'published' || publication.state === 'warning')) ? (
                          <p className="distribution-publication__help">
                            {publicationStateHelp[publication.state]}
                            {publication.remote_status === 'not_reported' &&
                            (publication.state === 'published' || publication.state === 'warning')
                              ? `${publicationStateHelp[publication.state] ? ' ' : ''}This channel publishes no per-product status, so this means the product is in the feed Commerce Ops serves.`
                              : ''}
                          </p>
                        ) : null}

                        {publication.published_version > 0 ? (
                          <p className="quiet-empty">
                            Sent version {publication.published_version}
                            {publication.desired_version > publication.published_version
                              ? ` (current ${publication.desired_version})`
                              : ''}
                            {' · '}
                            {publication.last_success_at
                              ? `Last send ${formatWhen(publication.last_success_at)}`
                              : 'No successful send recorded'}
                          </p>
                        ) : null}

                        <IssueList issues={publication.issues} />

                        <div className="distribution-publication__actions">
                          <button
                            type="button"
                            className="link-button"
                            onClick={() =>
                              setPreviewFor(previewFor === publication.connection_id ? null : publication.connection_id)
                            }
                            aria-expanded={previewFor === publication.connection_id}
                          >
                            {previewFor === publication.connection_id ? 'Hide what is sent' : 'Show what is sent'}
                          </button>
                          {canManage && (publication.state === 'failed' || publication.state === 'rejected') ? (
                            <button
                              type="button"
                              className="link-button"
                              disabled={busy === publication.id}
                              onClick={() => act(publication.id, () => retryDistributionPublication(publication.id))}
                            >
                              {busy === publication.id ? 'Retrying' : 'Retry'}
                            </button>
                          ) : null}
                        </div>

                        {previewFor === publication.connection_id ? (
                          <PayloadPreviewPanel projectionId={projectionId} connectionId={publication.connection_id} />
                        ) : null}
                      </article>
                    ))}
                  </div>
                )}
              </section>
            </>
          ) : null}
    </Drawer>
  );
}

/** IssueList states each issue, its severity in words, and where to fix it. */
function IssueList({ issues }: { issues: DistributionIssue[] }) {
  if (issues.length === 0) return null;
  return (
    <ul className="distribution-issues">
      {issues.map((issue) => (
        <li key={`${issue.code}-${issue.field ?? ''}`} className={`distribution-issue distribution-issue--${issue.severity}`}>
          <LevelBadge level={severityLevel[issue.severity]}>{severityLabel[issue.severity]}</LevelBadge>
          <div>
            <p className="distribution-issue__message">{issue.message}</p>
            {issue.source_field ? (
              <p className="distribution-issue__source">
                Source field: <code>{issue.source_field}</code>
              </p>
            ) : null}
            {issue.advice ? <p className="distribution-issue__advice">{issue.advice}</p> : null}
            {issue.documentation ? (
              <p>
                <a href={issue.documentation} target="_blank" rel="noreferrer noopener">
                  What the channel says about this
                </a>
              </p>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * The payload preview: the exact bytes the channel receives, and where
 * every field came from. It contains no credential — a payload is built
 * from product data alone.
 */
function PayloadPreviewPanel({ projectionId, connectionId }: { projectionId: string; connectionId: string }) {
  const preview = useResource<PayloadPreview>(
    () => previewDistributionPayload(projectionId, connectionId),
    [projectionId, connectionId],
    `${projectionId}:${connectionId}`,
  );

  if (preview.loading && !preview.data) return <LoadingRows label="Building the payload" rows={3} />;
  if (preview.error) return <ErrorNotice error={preview.error} />;
  if (!preview.data) return null;

  return (
    <div className="distribution-preview">
      {preview.data.provenance.length > 0 ? (
        <Table label="Where each published field comes from" compact className="distribution-provenance-table">
          <caption className="visually-hidden">Where each published field comes from</caption>
          <thead>
            <tr>
              <th scope="col">Channel field</th>
              <th scope="col">Comes from</th>
              <th scope="col">Value</th>
            </tr>
          </thead>
          <tbody>
            {preview.data.provenance.map((entry) => (
              <tr key={entry.external_field}>
                <th scope="row">
                  <code>{entry.external_field}</code>
                </th>
                <td>
                  {entry.canonical_field}
                  {entry.source_field ? (
                    <>
                      {' ← '}
                      <code>{entry.source_field}</code>
                    </>
                  ) : null}
                  {entry.note ? <span className="distribution-issue__source"> {entry.note}</span> : null}
                </td>
                <td>{entry.value || '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      <pre className="distribution-preview__body" aria-label="Payload sent to the channel">
        <code>{preview.data.body}</code>
      </pre>
    </div>
  );
}
